/**
 * Back/Forward scroll restoration for the SRP results list.
 *
 * The SRP doesn't scroll the window: SearchWrapper's main column (`[data-srp-scroll-region]`) is
 * its own overflow-y:auto scroller, at every width. Vike only saves/restores window scroll, and the
 * region is a new element every time the SRP re-mounts, so Back from a job page always landed at
 * the top of the results. That also broke the reverse card morph for any card below the first
 * screen (rearmSrpCardFromStorage skips cards outside the viewport).
 *
 * We save the region's scrollTop, keyed by the SRP URL, whenever a navigation leaves the page, and
 * put it back after a history (Back/Forward) navigation re-renders that URL.
 */

export const SRP_SCROLL_REGION_SELECTOR = '[data-srp-scroll-region]';
const STORAGE_KEY = 'jc-srp-scroll';
const MAX_SAVED = 20;
/** How long to wait for the re-mounted results to be tall enough to scroll back to. */
const RESTORE_TIMEOUT_MS = 600;

/**
 * `top` is the raw scrollTop (fallback). `anchorJobId`/`anchorOffset` record the clicked (or
 * first visible) card and its distance from the top of the viewport: content above the list (the top-employers /
 * top-roles strips, patched in after the first render) can still be growing when we restore, so
 * putting that card back at the same offset is what actually returns the visitor to the same view.
 */
type SavedPosition = { top: number; at: number; anchorJobId?: string; anchorOffset?: number };
type SavedPositions = Record<string, SavedPosition>;

function urlKey(href: string): string {
  try {
    const url = new URL(href);
    return `${url.pathname}${url.search}`;
  } catch {
    return href;
  }
}

function readSaved(): SavedPositions {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' ? (parsed as SavedPositions) : {};
  } catch {
    return {};
  }
}

function writeSaved(saved: SavedPositions): void {
  try {
    const entries = Object.entries(saved).sort((a, b) => b[1].at - a[1].at).slice(0, MAX_SAVED);
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    /* sessionStorage unavailable (private mode, quota): restoration just doesn't happen */
  }
}

function getScrollRegion(): HTMLElement | null {
  const region = document.querySelector(SRP_SCROLL_REGION_SELECTOR);
  if (!(region instanceof HTMLElement)) return null;
  // Map view and other layouts that don't scroll the region have nothing to restore.
  const overflowY = window.getComputedStyle(region).overflowY;
  return overflowY === 'auto' || overflowY === 'scroll' ? region : null;
}

/** Call while the SRP is still the current page (before Vike swaps it out and changes the URL). */
export function saveSrpScrollPosition(): void {
  if (typeof window === 'undefined') return;
  const region = getScrollRegion();
  // A hidden (kept-alive, see srp-keep-alive.tsx) SRP isn't the page being left; saving its
  // display:none scroller would overwrite the real position with 0.
  if (!region || region.closest<HTMLElement>('#srp-keepalive')?.style.display === 'none') return;
  const regionRect = region.getBoundingClientRect();
  let anchorJobId: string | undefined;
  let anchorOffset: number | undefined;
  // Anchor on the card that was just clicked (marked by armJobCardTransition) when it's in view:
  // that's the one the visitor is looking for on the way back, and anchoring on it keeps it in
  // place even if a card between it and the top of the view changes height. Otherwise the first
  // card in view.
  const clicked = region.querySelector<HTMLElement>('article.jc-vt-on[data-job-id]');
  const clickedRect = clicked?.getBoundingClientRect();
  const candidates: HTMLElement[] =
    clicked && clickedRect && clickedRect.bottom > regionRect.top && clickedRect.top < regionRect.bottom
      ? [clicked]
      : Array.from(region.querySelectorAll<HTMLElement>('article[data-job-id]'));
  for (const card of candidates) {
    const rect = card.getBoundingClientRect();
    if (rect.bottom > regionRect.top) {
      anchorJobId = card.dataset.jobId;
      // Relative to the viewport, not the region: on mobile the SRP header collapses while the
      // list is scrolled and comes back expanded after Back, moving the region's top edge ~19px.
      anchorOffset = Math.round(rect.top);
      break;
    }
  }
  const saved = readSaved();
  saved[urlKey(window.location.href)] = { top: Math.round(region.scrollTop), at: Date.now(), anchorJobId, anchorOffset };
  writeSaved(saved);
}

function findCard(region: HTMLElement, jobId: string): HTMLElement | null {
  // Iterate rather than build a selector from a stored id (same reasoning as job-card-transition.ts).
  for (const card of region.querySelectorAll<HTMLElement>('article[data-job-id]')) {
    if (card.dataset.jobId === jobId) return card;
  }
  return null;
}

/**
 * After a history navigation has rendered, scroll the SRP region back to where it was for the
 * current URL. Resolves once restored, or once it gives up (no saved position, no region, or the
 * list never grew tall enough within RESTORE_TIMEOUT_MS, in which case it scrolls as far as it can).
 */
export function restoreSrpScrollPosition(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  const saved = readSaved()[urlKey(window.location.href)];
  if (!saved || (!saved.top && !saved.anchorJobId)) return Promise.resolve();
  const startedAt = performance.now();
  return new Promise((resolve) => {
    const attempt = () => {
      // The SRP is up but its results don't scroll (map view): nothing to restore, don't stall
      // the Back transition waiting for a scroller that won't appear.
      if (document.querySelector(SRP_SCROLL_REGION_SELECTOR) && !getScrollRegion()) {
        resolve();
        return;
      }
      const region = getScrollRegion();
      const timedOut = performance.now() - startedAt >= RESTORE_TIMEOUT_MS;
      if (region) {
        const anchor = saved.anchorJobId ? findCard(region, saved.anchorJobId) : null;
        const maxTop = region.scrollHeight - region.clientHeight;
        if (anchor && typeof saved.anchorOffset === 'number') {
          const target = region.scrollTop + anchor.getBoundingClientRect().top - saved.anchorOffset;
          if (maxTop >= target || timedOut) {
            region.scrollTop = Math.min(Math.max(0, target), Math.max(0, maxTop));
            resolve();
            return;
          }
        } else if (!saved.anchorJobId && (maxTop >= saved.top || timedOut)) {
          region.scrollTop = Math.min(saved.top, Math.max(0, maxTop));
          resolve();
          return;
        } else if (timedOut) {
          // The anchor card isn't in this render (results changed): fall back to the raw offset.
          region.scrollTop = Math.min(saved.top, Math.max(0, maxTop));
          resolve();
          return;
        }
      } else if (timedOut) {
        resolve();
        return;
      }
      // setTimeout, not requestAnimationFrame: this runs while the Back view transition is waiting
      // on its update callback, and the browser skips rendering (so rAF never fires) until then.
      setTimeout(attempt, 16);
    };
    attempt();
  });
}
